import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, Phase } from "../lib/api";
import { useAuthStore } from "../store/auth";
import {
  CheckCircleIcon,
  LockClosedIcon,
  PlayCircleIcon,
  ChartBarIcon,
  EyeIcon,
  DocumentTextIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [phases, setPhases] = useState<Phase[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPhases();
  }, []);

  const loadPhases = async () => {
    try {
      const response = await api.listPhases();
      setPhases(response.phases);
    } catch (error) {
      console.error("Failed to load phases:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-copper-500"></div>
      </div>
    );
  }

  const completedCount = phases.filter((p) => p.status === "completed").length;
  const progress = Math.round((completedCount / phases.length) * 100);

  const currentPhase = phases.find(p => p.status === 'available' || p.status === 'pending_review') || phases[phases.length - 1];
  const reviewCount = phases.filter(p => p.status === 'pending_review').length;

  return (
    <div className="space-y-8">
      {/* Welcome Header with Stats */}
      <div className="relative overflow-hidden rounded-2xl bg-white border border-charcoal-100 shadow-sm">
        {/* Decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-[radial-gradient(circle,rgba(196,117,76,0.06),transparent_70%)]" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-[radial-gradient(circle,rgba(90,123,111,0.05),transparent_70%)]" />
          {/* Subtle topo lines */}
          <svg className="absolute right-0 top-0 w-48 h-48 opacity-[0.03]" viewBox="0 0 200 200">
            <circle cx="150" cy="50" r="80" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="150" cy="50" r="60" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
            <circle cx="150" cy="50" r="40" fill="none" stroke="#2C3E3D" strokeWidth="0.5" />
          </svg>
        </div>

        {/* Gradient accent strip */}
        <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />

        <div className="relative p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-charcoal-500">
                Welcome back, {user?.business_name}!
              </h1>
              <p className="text-charcoal-300 mt-2">
                {progress === 100
                  ? "Your marketing system is complete! Head to Activation to launch."
                  : `Your marketing system is ${progress}% complete`}
              </p>
            </div>
            <div className="flex items-center space-x-2 text-copper-500">
              <ChartBarIcon className="h-8 w-8" />
              <span className="text-3xl font-bold">
                {completedCount}/{phases.length}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="w-full bg-charcoal-100 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-copper-400 via-copper-500 to-sage-500 h-3 rounded-full transition-all duration-500 relative"
                style={{ width: `${progress}%` }}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite] rounded-full" />
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-xl bg-sage-50 border border-sage-100">
              <p className="text-2xl font-bold text-sage-600">{completedCount}</p>
              <p className="text-xs text-charcoal-300 mt-1">Phases Done</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-copper-50 border border-copper-100">
              <p className="text-2xl font-bold text-copper-500">{reviewCount}</p>
              <p className="text-xs text-charcoal-300 mt-1">Ready to Review</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-teal-50 border border-teal-100">
              <p className="text-2xl font-bold text-teal-600">{currentPhase?.number || '-'}</p>
              <p className="text-xs text-charcoal-300 mt-1">Current Phase</p>
            </div>
          </div>
        </div>
      </div>

      {/* Journey Timeline */}
      <div className="relative overflow-hidden rounded-2xl bg-white border border-charcoal-100 shadow-sm">
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute left-0 bottom-0 w-full h-16 opacity-[0.03]" viewBox="0 0 1200 60" preserveAspectRatio="none">
            <path d="M0,60 L0,40 L200,20 L400,35 L600,10 L800,30 L1000,15 L1200,45 L1200,60 Z" fill="#2C3E3D" />
          </svg>
        </div>
        <div className="relative p-6 overflow-x-auto">
          <h2 className="text-lg font-bold text-charcoal-500 mb-4">Your Journey</h2>
          <div className="flex items-center min-w-[640px] py-2">
            {phases.map((phase, idx) => {
              const isCompleted = phase.status === 'completed';
              const isCurrent = phase.status === 'available' || phase.status === 'pending_review';
              const isLast = idx === phases.length - 1;
              return (
                <div key={phase.number} className="flex items-center flex-1">
                  <Link
                    to={phase.status !== 'locked' ? `/phases/${phase.slug}` : '#'}
                    className="flex flex-col items-center group relative"
                    title={`Phase ${phase.number}: ${phase.name}`}
                  >
                    <div
                      className={clsx(
                        'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all',
                        isCompleted && 'bg-sage-500 text-white shadow-sm shadow-sage-500/20',
                        isCurrent && 'bg-copper-500 text-white ring-4 ring-copper-200 animate-pulse shadow-md shadow-copper-500/20',
                        !isCompleted && !isCurrent && 'bg-charcoal-100 text-charcoal-300'
                      )}
                    >
                      {isCompleted ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        phase.number
                      )}
                    </div>
                    <span
                      className={clsx(
                        'text-xs mt-2 text-center max-w-[80px] leading-tight',
                        isCompleted && 'text-sage-600 font-medium',
                        isCurrent && 'text-copper-600 font-bold',
                        !isCompleted && !isCurrent && 'text-charcoal-300'
                      )}
                    >
                      {phase.name}
                    </span>
                  </Link>
                  {!isLast && (
                    <div
                      className={clsx(
                        'flex-1 h-1 mx-1 rounded-full mt-[-20px] transition-all',
                        isCompleted ? 'bg-gradient-to-r from-sage-400 to-sage-300' : 'bg-charcoal-100'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Callout */}
      {reviewCount > 0 && (
        <div className="bg-gradient-to-r from-copper-50 to-cream-50 border border-copper-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-copper-100 rounded-lg">
              <EyeIcon className="h-5 w-5 text-copper-500" />
            </div>
            <p className="text-charcoal-500">
              <span className="font-bold">{reviewCount} phase{reviewCount > 1 ? 's' : ''}</span> ready for your review
            </p>
          </div>
          <Link
            to={`/phases/${phases.find(p => p.status === 'pending_review')?.slug}/review`}
            className="btn btn-primary text-sm"
          >
            Review Now
          </Link>
        </div>
      )}

      {progress === 100 && (
        <div className="bg-gradient-to-r from-sage-50 to-teal-50 border border-sage-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sage-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-sage-500" />
            </div>
            <p className="text-charcoal-500">
              <span className="font-bold">All phases complete!</span> Your marketing system is ready to launch.
            </p>
          </div>
          <Link to="/activation" className="btn btn-primary text-sm">
            Go to Activation
          </Link>
        </div>
      )}

      {/* Phases Grid */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-charcoal-500">Phase Details</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {phases.map((phase) => (
            <PhaseCard key={phase.number} phase={phase} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PhaseCard({ phase }: { phase: Phase }) {
  const statusConfig: Record<
    string,
    {
      icon: typeof CheckCircleIcon;
      iconColor: string;
      bgColor: string;
      borderColor: string;
      badge: string;
      badgeText: string;
      glowColor: string;
    }
  > = {
    completed: {
      icon: CheckCircleIcon,
      iconColor: "text-sage-500",
      bgColor: "bg-sage-50",
      borderColor: "border-sage-400",
      badge: "badge-sage",
      badgeText: "Completed",
      glowColor: "from-sage-400 to-sage-500",
    },
    pending_review: {
      icon: EyeIcon,
      iconColor: "text-copper-500",
      bgColor: "bg-copper-50",
      borderColor: "border-copper-400",
      badge: "badge-copper",
      badgeText: "Ready for Review",
      glowColor: "from-copper-400 to-copper-500",
    },
    available: {
      icon: PlayCircleIcon,
      iconColor: "text-teal-500",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-300",
      badge: "badge-teal",
      badgeText: "Ready",
      glowColor: "from-teal-400 to-teal-500",
    },
    locked: {
      icon: LockClosedIcon,
      iconColor: "text-charcoal-300",
      bgColor: "bg-charcoal-50",
      borderColor: "border-charcoal-200",
      badge: "badge-gray",
      badgeText: "Locked",
      glowColor: "from-charcoal-200 to-charcoal-300",
    },
  };

  const config = statusConfig[phase.status] || statusConfig.locked;
  const Icon = config.icon;
  const hasContent =
    phase.status === "completed" || phase.status === "pending_review";
  const reviewPath =
    phase.slug === "blog"
      ? "/phases/blog/review"
      : `/phases/${phase.slug}/review`;

  return (
    <div
      className={clsx(
        "relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm hover:shadow-md transition-all duration-200 border-l-4 flex flex-col group",
        config.borderColor,
        phase.status === "locked" && "opacity-60"
      )}
    >
      {/* Subtle corner decoration for non-locked cards */}
      {phase.status !== 'locked' && (
        <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none opacity-[0.04]">
          <svg viewBox="0 0 64 64" className="w-full h-full">
            <circle cx="64" cy="0" r="48" fill="none" stroke="#2C3E3D" strokeWidth="1" />
            <circle cx="64" cy="0" r="32" fill="none" stroke="#2C3E3D" strokeWidth="0.7" />
          </svg>
        </div>
      )}

      <Link
        to={`/phases/${phase.slug}`}
        className={clsx(
          "block flex-1 p-6",
          phase.status === "locked" &&
            "cursor-not-allowed pointer-events-none"
        )}
      >
        <div className="flex items-start justify-between mb-4">
          <div className={clsx("p-2 rounded-xl", config.bgColor)}>
            <Icon className={clsx("h-6 w-6", config.iconColor)} />
          </div>
          <span className={config.badge}>{config.badgeText}</span>
        </div>

        <h3 className="text-lg font-bold text-charcoal-500 mb-2">
          Phase {phase.number}: {phase.name}
        </h3>

        <p className="text-sm text-charcoal-300 line-clamp-2">
          {phase.description}
        </p>
      </Link>

      {hasContent && (
        <div className="px-6 pb-5 pt-0">
          <div className="pt-4 border-t border-charcoal-100 flex items-center justify-end">
            <Link
              to={reviewPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-copper-500 text-white hover:bg-copper-600 transition-colors shadow-sm shadow-copper-500/20"
            >
              <DocumentTextIcon className="h-3.5 w-3.5" />
              View Your Materials
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
